
import { defineAsyncComponent } from "vue";
import { Options, Vue } from "vue-class-component";
import DetailDeliverySection from "@/app/ui/views/shipment/booking/detail-booking/detail-delivery-section.vue";
import DetailItemSection from "@/app/ui/views/shipment/booking/detail-booking/detail-item-section.vue";
import DetailPaymentSection from "@/app/ui/views/shipment/booking/detail-booking/detail-payment-section.vue";
import DetailFtzSection from "@/app/ui/views/shipment/booking/detail-booking/detail-ftz-section.vue";
import AdditionalInformationSection from "@/app/ui/views/shipment/booking/detail-booking/additional-information-section.vue";
import OverlayPanel from "primevue/overlaypanel";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Utils";
import { GTMOnProcessSttEvent } from "@/app/infrastructures/misc/gtm-event/custom-events/on-process-stt-events";
import { PodDexController } from "@/app/ui/controllers/PodDexController";
import { ReasonListDex } from "@/data/payload/api/PodDexRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { prefixDisableDimensionC1, resolveAdjustEditPath } from "@/app/ui/views/shipment/booking/booking-utils";
import mapProductTypeName from "@/app/infrastructures/misc/common-library/ProductTypeNameMapper";
import { debounce } from "lodash";
import { SttDetailRequest } from "@/data/payload/api/BookingRequest";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { isShipmentCa } from "@/app/ui/modules/receipt-v2/module";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { DistrictData, CountryData } from "@/domain/entities/Location";
import { configPrice } from '@/app/infrastructures/misc/common-library/modules/Price';
const DetailRejectPopup = defineAsyncComponent({
  loader: () =>
    import(
      "@/app/ui/views/shipment/booking/detail-booking/detail-reject-popup.vue"
    )
});
@Options({
  components: {
    DetailDeliverySection,
    DetailItemSection,
    DetailPaymentSection,
    DetailFtzSection,
    DetailRejectPopup,
    OverlayPanel,
    AdditionalInformationSection
  }
})
class Detail extends Vue {
  isShowInfoDetail = false;
  private debouncedGetBookingDetail = debounce(
    async (id: number, sttNo: string, isAuth: boolean, pageType: string) => {
      try {
        ShipmentBookingController.setLoadingDetailSTT(true);

        await ShipmentBookingController.getSttDetail(
          new SttDetailRequest({
            sttNo: sttNo,
            isWithEligibility: true,
            isAuth: isAuth,
            pageType: pageType
          })
        );
      } catch (error) {
        console.error("Error getBookingDetail:", error);
      } finally {
        ShipmentBookingController.setLoadingDetailSTT(false);
      }
    },
    500
  );

  onCheckEligibleShowDetail() {
    if (this.isShowInfoDetail) return this.onChangeToggle();
    if (this.isEligibleToShowDetail) this.onChangeToggle();
    else
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          customWidth: "417px",
          imageWidth: "32",
          image: "badge-exit-confirmation",
          title: this.$t("trackDelivery.dataNotAvailableTitle"),
          message: this.$t("trackDelivery.dataNotAvailableMessage"),
          textSuccess: this.$t("trackDelivery.understood"),
          customTitleMessagePadding: "pt-4 pb-6 px-10",
          customButton: "py-3 font-medium",
          onClose: () => {
            MainAppController.closeMessageModal();
          },
          onSubmit: () => {
            MainAppController.closeMessageModal();
          }
        })
      );
  }

  onChangeToggle() {
    this.isShowInfoDetail = !this.isShowInfoDetail;
    this.debouncedGetBookingDetail(
      this.id,
      this.sttNumber,
      this.isAuth,
      this.isShowInfoDetail ? "stt_detail" : "stt_detail_un_show"
    );
  }
  get getTitle() {
    return `STT : ${this.detailData.sttNo}`;
  }
  get isEditAble() {
    return (
      FlagsPermissionBooking.permission_booking_edit.isEnabled() &&
      this.detailData.sttIsAllowEdit
    );
  }
  get statusChipsStyle() {
    return {
      label: `${this.detailData.sttLastStatus?.toUpperCase()} (${
        this.detailData.sttLastStatusCounter
      }/${this.detailData.sttTotalPiece})`,
      title: "Current Status",
      color: "gray-lp-200",
      textColor: "black-lp-300",
      toolTip: true
    };
  }
  get statusTroubleChipsStyle() {
    return {
      label: this.detailData.sttWarningStatus
        ? this.detailData.sttWarningStatus?.toUpperCase()
        : "-",
      title: this.$t("bookingShipment.detail.troubledStatus"),
      color: this.detailData.sttWarningStatus ? "gray-lp-200" : "white",
      textColor: "black-lp-300",
      toolTip: !!this.detailData.sttWarningStatus
    };
  }
  get isMigratingFromElexys() {
    return process.env.VUE_APP_MIGRATE_FROM_ELEXYS === "true";
  }

  get formAccountCurrencyPrefix(): string {
    const currency = this.countryList.find(country => country.code === this.detailData.sttDestinationCountryCode)?.currency || "";
    return configPrice[currency]?.prefix ?? "Rp";
  }

  // Get Data
  created() {
    this.getDetail();
    this.getCountryList();
  }

  get id(): number {
    return Number(0);
  }

  get sttNumber(): string {
    return String(this.$route.params.id);
  }

  get isAuth(): any {
    return this.$route?.meta?.isAuth;
  }

  isEligibleToShowDetail = false;
  originCountryCode = "";
  async getDetail() {
    const resp = await ShipmentBookingController.getSttDetail(
      new SttDetailRequest({
        sttNo: this.sttNumber,
        isWithEligibility: true,
        isAuth: this.isAuth,
        pageType: "stt_detail_un_show"
      })
    );

    this.isEligibleToShowDetail = resp.isEligibleToShowDetail || false;

    await this.$nextTick();

    const origin = (await LocationController.getDetailDistrictByDistrictCode({
      districtCode: this.detailData.sttOriginDistrictId
    })) as DistrictData;

    if (origin instanceof DistrictData) {
      this.originCountryCode = origin.countryCode;
    }
  }

  // route navigation
  goBack() {
    if (this.$route?.meta?.customPath === "lacak-pengiriman") {
      this.$router.push(
        `${
          IS_SENDER_ACCOUNT ? "" : "/shipment"
        }/lacak-pengiriman/pengiriman-aktif`
      );
    } else {
      this.$router.push(this.customPath);
    }
  }
  get customPath() {
    return this.$route?.meta?.customPath
      ? `/shipment/${this.$route?.meta?.customPath}`
      : `${this.$route?.meta?.bookingPath}`;
  }

  // Handle Get
  get detailData() {
    return ShipmentBookingController.shipmentBookingDetail;
  }
  get isLoading() {
    return ShipmentBookingController.isLoading;
  }
  get isError() {
    return ShipmentBookingController.isError;
  }
  get errorCause() {
    return ShipmentBookingController.isErrorCause;
  }

  // handle overlay panel
  isRejectPopup = false;
  toolTip1 = false;
  toolTip2 = false;
  toggleStatusPanel(event: any, name: string) {
    const refs: any = this.$refs;
    if (name === "tooltipStatus") {
      this.toolTip1 = !this.toolTip1;
    } else {
      this.toolTip2 = !this.toolTip2;
    }
    refs[name].toggle(event);
  }

  get isCountryMy() {
    return AccountController.accountData.countryCode === "my";
  }

  // handle print
  async print(type: string) {
    const language =
      AccountController.accountData.accountIsForeign || this.isCountryMy
        ? "en"
        : "id";

    const book: any = await import(
      "@/app/ui/views/shipment/booking/create-booking/payment-detail/book.vue"
    );
    const printType: any = {
      thermal: () =>
        book.default.methods.printThermal(
          this.id,
          language,
          this.detailData.sttNo
        ),
      "thermal-pdf": () =>
        book.default.methods.printBasic(
          this.id,
          0,
          language,
          false,
          true,
          this.detailData.sttNo
        ),
      basic: () =>
        book.default.methods.printBasic(
          this.id,
          1,
          language,
          undefined,
          undefined,
          this.detailData.sttNo
        ),
      "commercial-invoice-a4": () => {
        if (this.detailData.isNewForm) {
          book.default.methods.directPrintInterpack(
            this.id,
            this.detailData.sttNo,
            "a4"
          );
        } else {
          book.default.methods.directPrintInterpackV1(
            this.id,
            this.detailData.sttNo
          );
        }
      },
      "commercial-invoice-a6": () => {
        if (this.detailData.isNewForm) {
          book.default.methods.directPrintInterpack(
            this.id,
            this.detailData.sttNo,
            "a6"
          );
        }
      }
    };

    printType[type]();
  }

  goToEdit(id: any, sttNo: any) {
    GTMOnProcessSttEvent("on_process_stt_edit", {
      statusStt: this.detailData.sttLastStatus,
      sttNumber: this.detailData.sttNo
    });
    this.$router.push({
      path: resolveAdjustEditPath({
        bookingFormVersion: this.detailData.bookingFormVersion,
        isNewForm: this.detailData.isNewForm,
        basePath: this.customPath,
        sttNo
      })
    });
  }

  async showCancelConfirmation() {
    GTMOnProcessSttEvent("on_process_stt_cnx", {
      sttNumber: this.detailData.sttNo
    });
    if (!IS_SENDER_ACCOUNT) {
      MainAppController.showLoading();
      await PodDexController.getReasonList(
        new ReasonListDex({
          statusCode: "CNX",
          reasonStatus: "active",
          isNotShownEnable: this.detailData.sttLastStatus === "STI"
        })
      );
      MainAppController.closeLoading();
    }
    this.isRejectPopup = true;
  }

  formatDate(date: string) {
    return formatDate(date);
  }

  get isRejectedStatus(): boolean {
    return !!this.detailData.sttLastStatus.match(/^REJECTED$/i);
  }

  get isFtzAccount() {
    if (IS_SENDER_ACCOUNT) return false;
    if (
      this.detailData.sttDestinationCountryCode !==
      this.detailData.sttOriginCountryCode
    )
      return false;
    return !!(
      (AccountController.accountData?.account_location?.city_free_trade_zone ===
        "yes" ||
        this.detailData.sttPiecePerPack ||
        this.detailData.sttNextCommodity ||
        this.detailData.isSttFtz ||
        this.detailData.sttFtzCipl.length) &&
      !IS_SENDER_ACCOUNT &&
      this.detailData.isSttFtz
    );
  }

  get isSenderAcount() {
    return IS_SENDER_ACCOUNT;
  }

  get isTempCondition(): boolean {
    return !(
      this.detailData.sttIsCod &&
      !this.detailData.sttIsDfod &&
      this.detailData.sttClient.clientCodConfigAmount ===
        "goods_price,total_tarif"
    );
  }

  get productTypeName() {
    return mapProductTypeName(this.detailData.sttProductTypeName);
  }

  get isInterpack() {
    return ["INTERPACK", "INTERPACKMAXI"].includes(
      this.detailData.sttProductTypeName
    );
  }

  get isInternationalShipment() {
    return this.detailData.isInternational;
  }

  get canPrintCommercialInvoiceA4() {
    return this.detailData.isInternational === true;
  }

  get canPrintCommercialInvoiceA6() {
    return (
      this.detailData.isInternational === true &&
      this.detailData.isNewForm === true
    );
  }

  get eligibleAdditionalInfo() {
    return FlagsMedusa.flag_release_interpack_additional_info.isEnabled();
  }

  get isShipmentC1() {
    return this.detailData.sttShipmentId.match(/^C1/gi);
  }

  get allowEditC1Shipment() {
    if (AccountController.accountData.isPosAccount) {
      return (
        prefixDisableDimensionC1(
          String(AccountController.accountData.account_id)
        ) && this.allowAllEdit
      );
    }

    if (AccountController.accountData.isConsoleAccount) {
      return false;
    }

    return this.allowAllEdit;
  }

  get allowAllEdit() {
    return this.isEditAble && !this.isSenderAcount && this.isTempCondition;
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get attachMixFiles() {
    return [
      this.detailData.sttKtpImage,
      this.detailData.sttTaxImage,
      ...this.detailData.sttAttachFiles
    ].filter((file) => file);
  }

  get ftzAttachmentMixFiles() {
    return [
      ...this.detailData.sttFtzAttachFiles,
      this.detailData.sttFtzKtpImage,
      this.detailData.sttFtzTaxImage
    ].filter((file) => file);
  }

  get isShipmentCaOldForm() {
    return (
      isShipmentCa(this.detailData.sttShipmentId) && !this.detailData.isNewForm
    );
  }

  get sttBookedByCountryMy() {
    return this.detailData.sttBookedByCountry.toLowerCase() === "my";
  }

  get commodityDetailValue() {
    return this.isShipmentCaOldForm && !!this.detailData.sttCipl.length
      ? this.detailData.sttCipl.map((item: any) => item.item_detail).join(", ")
      : this.detailData.sttCommodityDetail;
  }

  countryList: CountryData[] = [];
  async getCountryList() {
    const res = await LocationController.getCountryList({
      search: "",
      status: "active",
      page: 1,
      limit: 250
    });

    if (res) this.countryList = res.countryData;

  }
}
export default Detail;
